import type Luminary from '@lumen5/luminary';
import type { Asset, Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type { AppDispatch } from 'js/src/libs/RootState';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';
import { setAssetPositionTransformationOperation } from 'js/src/luminary/luminary-operations';

/**
 * Position an asset at a specific position, taking into account its anchor point.
 */
export function positionAsset(asset: Asset, position: { x: number; y: number }): void {
    const { width, height } = asset.getBounds();
    const anchorXOffset = asset.transformation.anchor.x * width;
    const anchorYOffset = asset.transformation.anchor.y * height;
    asset.transformation.position.x = position.x + anchorXOffset;
    asset.transformation.position.y = position.y + anchorYOffset;
}

export interface Bounds {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface ParsedInstruction {
    position?: { x?: number; y?: number };
    scale?: number;
}

/**
 * Parse edge-to-edge alignment from natural language with detailed error messages for LLM guidance
 */
export function parseEdgeAlignment(instruction: string): {
    assetAnchorX?: number;
    assetAnchorY?: number;
    boundsAnchorX?: number;
    boundsAnchorY?: number;
    error?: string;
} | null {
    const lower = instruction.toLowerCase().trim();

    // Check if this looks like an edge alignment attempt
    if (lower.includes('align') && (lower.includes('edge') || lower.includes('center'))) {
        // Check for missing 'asset' keyword
        if (!lower.includes('asset')) {
            return {
                error: "Edge alignment must specify 'asset' and 'bounds' explicitly. Use format: 'align asset [edge] to bounds [edge] [axis]'. Example: 'align asset left edge to bounds right edge horizontally'",
            };
        }

        // Check for missing 'bounds' keyword
        if (!lower.includes('bounds')) {
            return {
                error: "Edge alignment must specify 'bounds'. Use format: 'align asset [edge] to bounds [edge] [axis]'. Example: 'align asset left edge to bounds right edge horizontally'",
            };
        }

        // Check for missing axis
        if (!lower.includes('horizontally') && !lower.includes('vertically')) {
            return {
                error: "Edge alignment missing axis specification. Add 'horizontally' or 'vertically' at the end. Example: 'align asset left edge to bounds right edge horizontally'",
            };
        }

        // Try to parse with strict pattern
        const pattern =
            /align\s+asset\s+(left\s+edge|right\s+edge|center|top\s+edge|bottom\s+edge)\s+to\s+bounds\s+(left\s+edge|right\s+edge|center|top\s+edge|bottom\s+edge)\s+(horizontally|vertically)/;
        const match = lower.match(pattern);

        if (!match) {
            return {
                error: "Edge alignment format incorrect. Use: 'align asset [edge] to bounds [edge] [axis]'. Valid edges: left edge, right edge, center, top edge, bottom edge. Valid axes: horizontally, vertically",
            };
        }

        const assetEdge = match[1].replace(/\s+edge/, '').trim();
        const boundsEdge = match[2].replace(/\s+edge/, '').trim();
        const axis = match[3];

        // Validate edge/axis compatibility with specific guidance
        const horizontalEdges = ['left', 'center', 'right'];
        const verticalEdges = ['top', 'center', 'bottom'];

        if (axis === 'horizontally' && !horizontalEdges.includes(assetEdge)) {
            return {
                error: `Cannot use vertical edge '${match[1]}' with horizontal axis. For horizontal alignment, use: left edge, right edge, or center. Example: 'align asset left edge to bounds right edge horizontally'`,
            };
        }

        if (axis === 'vertically' && !verticalEdges.includes(assetEdge)) {
            return {
                error: `Cannot use horizontal edge '${match[1]}' with vertical axis. For vertical alignment, use: top edge, bottom edge, or center. Example: 'align asset top edge to bounds bottom edge vertically'`,
            };
        }

        if (axis === 'horizontally' && !horizontalEdges.includes(boundsEdge)) {
            return {
                error: `Cannot use vertical edge '${match[2]}' for bounds with horizontal axis. For horizontal alignment, use: left edge, right edge, or center. Example: 'align asset left edge to bounds right edge horizontally'`,
            };
        }

        if (axis === 'vertically' && !verticalEdges.includes(boundsEdge)) {
            return {
                error: `Cannot use horizontal edge '${match[2]}' for bounds with vertical axis. For vertical alignment, use: top edge, bottom edge, or center. Example: 'align asset top edge to bounds bottom edge vertically'`,
            };
        }

        // Successful parse
        const edgeToAnchor = {
            left: 0,
            center: 0.5,
            right: 1,
            top: 0,
            bottom: 1,
        };

        const result: {
            assetAnchorX?: number;
            assetAnchorY?: number;
            boundsAnchorX?: number;
            boundsAnchorY?: number;
        } = {};

        if (axis === 'horizontally') {
            result.assetAnchorX = edgeToAnchor[assetEdge as keyof typeof edgeToAnchor];
            result.boundsAnchorX = edgeToAnchor[boundsEdge as keyof typeof edgeToAnchor];
        } else if (axis === 'vertically') {
            result.assetAnchorY = edgeToAnchor[assetEdge as keyof typeof edgeToAnchor];
            result.boundsAnchorY = edgeToAnchor[boundsEdge as keyof typeof edgeToAnchor];
        }

        return result;
    }

    // Not an edge alignment instruction
    return null;
}

/**
 * Parse edge alignment instruction into position values
 * Only supports edge alignment format: "align asset [edge] to bounds [edge] [axis]"
 */
export function parseInstruction(
    instruction: string,
    bounds: Bounds,
    asset: Asset,
    margin_pixels?: number,
): ParsedInstruction {
    const result: ParsedInstruction = {};

    // Parse edge-to-edge alignment
    const edgeAlignment = parseEdgeAlignment(instruction);

    // Check for edge alignment parsing errors
    if (edgeAlignment?.error) {
        throw new Error(edgeAlignment.error);
    }

    // Only edge alignment is supported
    if (!edgeAlignment) {
        throw new Error(
            "Only edge alignment instructions are supported. Use format: 'align asset [edge] to bounds [edge] [axis]'. Example: 'align asset left edge to bounds right edge horizontally'",
        );
    }

    // Get asset dimensions
    const assetBounds = asset.getBounds();
    const assetWidth = assetBounds.width;
    const assetHeight = assetBounds.height;

    // Calculate anchor offsets based on alignment axis
    let anchorOffsetX = 0;
    let anchorOffsetY = 0;

    if (edgeAlignment.assetAnchorX !== undefined) {
        // Horizontal alignment - we're guaranteed to have both X anchors
        const edgeAnchorX = edgeAlignment.assetAnchorX;
        const transformAnchorX = asset.transformation.anchor.x;
        anchorOffsetX = (edgeAnchorX - transformAnchorX) * assetWidth;

        // Y axis uses current transform anchor (not aligned)
        anchorOffsetY = asset.transformation.anchor.y * assetHeight;
    } else if (edgeAlignment.assetAnchorY !== undefined) {
        // Vertical alignment - we're guaranteed to have both Y anchors
        const edgeAnchorY = edgeAlignment.assetAnchorY;
        const transformAnchorY = asset.transformation.anchor.y;
        anchorOffsetY = (edgeAnchorY - transformAnchorY) * assetHeight;

        // X axis uses current transform anchor (not aligned)
        anchorOffsetX = asset.transformation.anchor.x * assetWidth;
    }

    // Calculate position using bounds anchors
    const position: { x?: number; y?: number } = {};

    if (edgeAlignment.boundsAnchorX !== undefined) {
        // Horizontal alignment
        const boundsX = bounds.x + edgeAlignment.boundsAnchorX * bounds.width;
        position.x = boundsX - anchorOffsetX;
    }

    if (edgeAlignment.boundsAnchorY !== undefined) {
        // Vertical alignment
        const boundsY = bounds.y + edgeAlignment.boundsAnchorY * bounds.height;
        position.y = boundsY - anchorOffsetY;
    }

    // Apply margin if specified
    if (margin_pixels && Object.keys(position).length > 0) {
        if (edgeAlignment.assetAnchorX !== undefined) {
            // Horizontal alignment - apply margin based on which asset edge is aligned
            if (edgeAlignment.assetAnchorX === 0) {
                // Asset left edge is aligned - positive margin moves asset right (away from bounds)
                position.x! += margin_pixels;
            } else if (edgeAlignment.assetAnchorX === 1) {
                // Asset right edge is aligned - positive margin moves asset left (away from bounds)
                position.x! -= margin_pixels;
            } else {
                // Asset center is aligned - margin direction depends on bounds edge
                if (edgeAlignment.boundsAnchorX! <= 0.5) {
                    // Aligning to left side or center of bounds - move asset right
                    position.x! += margin_pixels;
                } else {
                    // Aligning to right side of bounds - move asset left
                    position.x! -= margin_pixels;
                }
            }
        }

        if (edgeAlignment.assetAnchorY !== undefined) {
            // Vertical alignment - apply margin based on which asset edge is aligned
            if (edgeAlignment.assetAnchorY === 0) {
                // Asset top edge is aligned - positive margin moves asset down (away from bounds)
                position.y! += margin_pixels;
            } else if (edgeAlignment.assetAnchorY === 1) {
                // Asset bottom edge is aligned - positive margin moves asset up (away from bounds)
                position.y! -= margin_pixels;
            } else {
                // Asset center is aligned - margin direction depends on bounds edge
                if (edgeAlignment.boundsAnchorY! <= 0.5) {
                    // Aligning to top side or center of bounds - move asset down
                    position.y! += margin_pixels;
                } else {
                    // Aligning to bottom side of bounds - move asset up
                    position.y! -= margin_pixels;
                }
            }
        }
    }

    // Set position if we have instructions for at least one axis
    if (Object.keys(position).length > 0) {
        result.position = position;
    }

    return result;
}

/**
 * Apply position transformation to an asset
 */
export function applyAssetPosition(
    scene: Scene,
    asset: Asset,
    positionInstruction: string,
    bounds: Bounds,
    luminary: Luminary,
    dispatch: AppDispatch,
    getState: GetRootState,
    margin_pixels?: number,
): { success: boolean; changes: string[]; error?: string } {
    try {
        // Parse the natural language position instruction
        const parsed = parseInstruction(positionInstruction, bounds, asset, margin_pixels);

        // Apply position if specified
        if (parsed.position) {
            const { setAssetPositionTransformation } = makeOperationsCallable(
                { setAssetPositionTransformationOperation },
                luminary,
                dispatch,
                getState,
            );

            // Get current position for axes not specified in instruction
            const currentX = asset.transformation.position.x;
            const currentY = asset.transformation.position.y;

            // Use parsed position if specified, otherwise keep current position
            const newX = parsed.position.x ?? currentX;
            const newY = parsed.position.y ?? currentY;

            setAssetPositionTransformation({
                sceneId: scene.id,
                assetId: asset.id,
                x: newX,
                y: newY,
            });

            // Create descriptive change message
            const positionChanges = [];
            if (parsed.position.x !== undefined) {
                positionChanges.push(`x: ${parsed.position.x}`);
            }
            if (parsed.position.y !== undefined) {
                positionChanges.push(`y: ${parsed.position.y}`);
            }

            return {
                success: true,
                changes: [`positioned (${positionChanges.join(', ')})`],
            };
        }

        return { success: true, changes: [] };
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown positioning error';
        return { success: false, changes: [], error: errorMessage };
    }
}

/**
 * Apply scale transformation to an asset
 */
export function applyAssetScale(
    asset: Asset,
    scalePercentage: number,
    scaleDimension: 'width' | 'height',
    bounds: Bounds,
    luminary: Luminary,
): { success: boolean; changes: string[]; error?: string } {
    try {
        if (!VideoData.isMediaAsset(asset)) {
            return { success: false, changes: [], error: 'Asset does not support scaling' };
        }

        luminary.batchRun(() => {
            // Get current asset dimensions
            const assetBounds = asset.getBounds();
            const dimension = scaleDimension || 'width'; // Default to width

            let targetSize: number;
            let assetSize: number;

            if (dimension === 'width') {
                targetSize = bounds.width * (scalePercentage / 100);
                assetSize = assetBounds.width;
            } else {
                targetSize = bounds.height * (scalePercentage / 100);
                assetSize = assetBounds.height;
            }

            const calculatedScale = targetSize / assetSize;

            const mediaAsset = asset;
            mediaAsset.scaleX = calculatedScale;
            mediaAsset.scaleY = calculatedScale;
        });

        return {
            success: true,
            changes: [`scaled to ${scalePercentage}% of bounds ${scaleDimension || 'width'}`],
        };
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown scaling error';
        return { success: false, changes: [], error: errorMessage };
    }
}
